(() => {
  'use strict';

  const w = window;
  const d = document;
  d.documentElement.dataset.kziStage = 'loaded';
  const CONFIG_NODE = d.getElementById('KloudzIntelligenceConfig');
  if (!CONFIG_NODE) return;

  let config;
  try { config = JSON.parse(CONFIG_NODE.textContent || '{}'); } catch (_) { return; }
  if (!config || config.enabled === false) return;

  const VERSION = '1.1.0';
  const STATE_KEY = 'kloudz_merchandising_state_1';
  const SESSION_KEY = 'kloudz_merchandising_session_1';
  const MAX_SEQUENCE = 32;
  const MAX_QUEUE = 100;
  const MAX_AFFINITY_KEYS = 40;
  const DAY = 86400000;
  const ARMS = ['balanced', 'freshness', 'value', 'discovery'];
  const surfaces = new Set();
  const rankedSurfaces = new WeakSet();
  const stableOrders = new WeakMap();
  const impressionKeys = new Set();
  const eventQueue = [];
  let eventTimer = 0;
  let pendingProduct = null;
  let privacy = { preferences: false, analytics: false };
  let storageMode = 'session only';
  let runtimeExploration = clampConfig(config.explorationRate, 0, .2, .08);
  let autonomyTimer = 0;
  let autonomyDebounce = 0;

  function clampConfig(value, min, max, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback;
  }

  const safeStore = (storage) => {
    try {
      const test = '__kzi_test__';
      storage.setItem(test, '1');
      storage.removeItem(test);
      return storage;
    } catch (_) { return null; }
  };
  const sessionStore = safeStore(w.sessionStorage);
  const localStore = safeStore(w.localStorage);
  const readJSON = (store, key) => {
    if (!store) return null;
    try { return JSON.parse(store.getItem(key) || 'null'); } catch (_) { return null; }
  };
  const writeJSON = (store, key, value) => {
    if (!store) return false;
    try { store.setItem(key, JSON.stringify(value)); return true; } catch (_) { return false; }
  };
  const removeKey = (store, key) => {
    if (!store) return;
    try { store.removeItem(key); } catch (_) {}
  };
  const uid = () => {
    try { return w.crypto.randomUUID(); } catch (_) { return `kzi-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`; }
  };
  const clean = (value, length = 80) => String(value == null ? '' : value).replace(/[<>\u0000-\u001f]/g, '').trim().slice(0, length);
  const lower = (value) => clean(value, 120).toLowerCase();
  const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value) || 0));
  const num = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
  const now = () => Date.now();
  const pagePath = () => `${location.pathname}`.slice(0, 160);

  const defaults = () => ({
    version: 1,
    createdAt: now(),
    updatedAt: now(),
    pageviews: 0,
    sequence: [],
    affinities: { types: {}, vendors: {}, tags: {} },
    price: { sum: 0, count: 0, min: 0, max: 0 },
    search: { count: 0, averageLength: 0, intents: {} },
    disliked: [],
    bandit: {},
    experiments: {},
    decisions: [],
    autonomy: { cycles: 0, actions: 0, rollbacks: 0, blocked: 0, lastDecisionAt: 0, lastFingerprint: '' },
    outcomes: { impressions: 0, clicks: 0, quickViews: 0, saves: 0, compares: 0, cartAdds: 0 }
  });

  const sanitizeMap = (value) => {
    const output = {};
    if (!value || typeof value !== 'object' || Array.isArray(value)) return output;
    Object.entries(value).slice(0, MAX_AFFINITY_KEYS).forEach(([key, score]) => {
      const safeKey = lower(key).slice(0, 64);
      if (safeKey) output[safeKey] = clamp(score, -12, 30);
    });
    return output;
  };

  const hydrate = (raw) => {
    const base = defaults();
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return base;
    base.createdAt = num(raw.createdAt) || base.createdAt;
    base.updatedAt = num(raw.updatedAt) || base.updatedAt;
    base.pageviews = clamp(raw.pageviews, 0, 100000);
    base.sequence = Array.isArray(raw.sequence) ? raw.sequence.slice(-MAX_SEQUENCE).map((item) => ({
      handle: clean(item.handle, 80), type: clean(item.type, 64), vendor: clean(item.vendor, 64),
      tags: Array.isArray(item.tags) ? item.tags.slice(0, 8).map((tag) => clean(tag, 48)) : [],
      price: clamp(item.price, 0, 100000000), action: clean(item.action, 24), at: num(item.at) || now()
    })) : [];
    base.affinities.types = sanitizeMap(raw.affinities && raw.affinities.types);
    base.affinities.vendors = sanitizeMap(raw.affinities && raw.affinities.vendors);
    base.affinities.tags = sanitizeMap(raw.affinities && raw.affinities.tags);
    if (raw.price && typeof raw.price === 'object') {
      base.price = {
        sum: clamp(raw.price.sum, 0, 10000000000), count: clamp(raw.price.count, 0, 100000),
        min: clamp(raw.price.min, 0, 100000000), max: clamp(raw.price.max, 0, 100000000)
      };
    }
    if (raw.search && typeof raw.search === 'object') {
      base.search.count = clamp(raw.search.count, 0, 100000);
      base.search.averageLength = clamp(raw.search.averageLength, 0, 100);
      base.search.intents = sanitizeMap(raw.search.intents);
    }
    base.disliked = Array.isArray(raw.disliked) ? raw.disliked.slice(-30).map((value) => clean(value, 80)) : [];
    base.bandit = raw.bandit && typeof raw.bandit === 'object' && !Array.isArray(raw.bandit) ? raw.bandit : {};
    base.experiments = raw.experiments && typeof raw.experiments === 'object' && !Array.isArray(raw.experiments) ? raw.experiments : {};
    base.decisions = Array.isArray(raw.decisions) ? raw.decisions.slice(-40).map((item) => ({
      id: clean(item.id, 48), at: num(item.at) || now(), surface: clean(item.surface, 48), action: clean(item.action, 32),
      reason: clean(item.reason, 80), confidence: clamp(item.confidence, 0, 1), result: clean(item.result, 32)
    })) : [];
    if (raw.autonomy && typeof raw.autonomy === 'object') {
      base.autonomy = {
        cycles: clamp(raw.autonomy.cycles, 0, 100000), actions: clamp(raw.autonomy.actions, 0, 100000),
        rollbacks: clamp(raw.autonomy.rollbacks, 0, 100000), blocked: clamp(raw.autonomy.blocked, 0, 100000),
        lastDecisionAt: num(raw.autonomy.lastDecisionAt) || 0, lastFingerprint: clean(raw.autonomy.lastFingerprint, 120)
      };
    }
    if (raw.outcomes && typeof raw.outcomes === 'object') {
      Object.keys(base.outcomes).forEach((key) => { base.outcomes[key] = clamp(raw.outcomes[key], 0, 10000000); });
    }
    return base;
  };

  const privacyAPI = () => w.Shopify && w.Shopify.customerPrivacy;
  const privacyCall = (name) => {
    const api = privacyAPI();
    try { return Boolean(api && typeof api[name] === 'function' && api[name]()); } catch (_) { return false; }
  };
  const refreshPrivacy = () => {
    privacy.preferences = privacyCall('preferencesProcessingAllowed');
    privacy.analytics = privacyCall('analyticsProcessingAllowed');
    storageMode = Boolean(config.localPersonalization && privacy.preferences && localStore) ? 'consented local + session' : 'session only';
    updateConsole({ storage: storageMode });
  };
  refreshPrivacy();
  d.documentElement.dataset.kziStage = 'privacy-ready';

  const session = (() => {
    const value = readJSON(sessionStore, SESSION_KEY);
    if (value && typeof value === 'object' && value.id) {
      value.autonomyActions = clampConfig(value.autonomyActions, 0, 1000, 0);
      return value;
    }
    const fresh = { id: uid(), startedAt: now(), lastSeenAt: now(), autonomyActions: 0 };
    writeJSON(sessionStore, SESSION_KEY, fresh);
    return fresh;
  })();

  let state = hydrate(readJSON(sessionStore, STATE_KEY) || (privacy.preferences ? readJSON(localStore, STATE_KEY) : null));
  state.pageviews += 1;
  d.documentElement.dataset.kziStage = 'state-ready';

  const persist = () => {
    state.updatedAt = now();
    session.lastSeenAt = now();
    writeJSON(sessionStore, STATE_KEY, state);
    writeJSON(sessionStore, SESSION_KEY, session);
    if (config.localPersonalization && privacy.preferences) writeJSON(localStore, STATE_KEY, state);
  };

  const deviceClass = () => {
    const width = Math.max(d.documentElement.clientWidth || 0, w.innerWidth || 0);
    return width < 680 ? 'small' : width < 1100 ? 'medium' : 'large';
  };
  const timeBucket = () => {
    const hour = new Date().getHours();
    return hour < 6 ? 'night' : hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  };
  const contextKey = () => `${clean(config.pageType || 'other', 32)}|${deviceClass()}|${state.pageviews > 1 ? 'returning' : 'new'}`;
  const hash = (value) => {
    let output = 2166136261;
    const input = String(value);
    for (let i = 0; i < input.length; i += 1) { output ^= input.charCodeAt(i); output = Math.imul(output, 16777619); }
    return (output >>> 0) / 4294967295;
  };

  const safeSnapshot = () => ({
    version: VERSION,
    context: { pageType: clean(config.pageType, 32), device: deviceClass(), time: timeBucket(), returning: state.pageviews > 1 },
    affinities: {
      types: Object.entries(state.affinities.types).sort((a,b) => b[1] - a[1]).slice(0, 8),
      vendors: Object.entries(state.affinities.vendors).sort((a,b) => b[1] - a[1]).slice(0, 8),
      tags: Object.entries(state.affinities.tags).sort((a,b) => b[1] - a[1]).slice(0, 12)
    },
    pricePreference: state.price.count ? { averageCents: Math.round(state.price.sum / state.price.count), observations: state.price.count } : null,
    search: { count: state.search.count, averageLength: Math.round(state.search.averageLength), intents: state.search.intents },
    recentTypes: state.sequence.slice(-12).map((item) => item.type).filter(Boolean),
    outcomes: { ...state.outcomes },
    autonomy: {
      mode: clean(config.autonomy?.mode || 'bounded_zero_touch', 32),
      actions: state.autonomy.actions,
      rollbacks: state.autonomy.rollbacks,
      blocked: state.autonomy.blocked,
      lastDecisionAt: state.autonomy.lastDecisionAt || null
    }
  });

  const endpoint = (path) => {
    if (!config.endpoint) return '';
    try {
      const base = new URL(String(config.endpoint).trim());
      if (base.protocol !== 'https:') return '';
      return `${base.href.replace(/\/+$/, '')}${path}`;
    } catch (_) { return ''; }
  };

  const remoteAllowed = () => Boolean(config.telemetry && privacy.analytics && endpoint('/v1/events'));
  const flushEvents = async (useBeacon = false) => {
    if (!remoteAllowed() || !eventQueue.length) return;
    const outgoing = eventQueue.splice(0, Math.min(eventQueue.length, 50));
    const body = JSON.stringify({ protocol: 'kloudz-commerce-events/1', events: outgoing });
    const url = endpoint('/v1/events');
    try {
      if (useBeacon && navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))) return;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Kloudz-Protocol': 'kloudz-commerce-events/1' },
        body, keepalive: true, credentials: 'omit'
      });
      if (!response.ok) throw new Error('event endpoint unavailable');
    } catch (_) {
      eventQueue.unshift(...outgoing.slice(-MAX_QUEUE));
      eventQueue.splice(MAX_QUEUE);
    }
  };

  const emit = (name, payload = {}) => {
    const event = {
      name: clean(name, 42), at: new Date().toISOString(), session: session.id,
      page: { type: clean(config.pageType, 32), path: pagePath() },
      context: { device: deviceClass(), time: timeBucket() },
      payload
    };
    eventQueue.push(event);
    if (eventQueue.length > MAX_QUEUE) eventQueue.shift();
    d.dispatchEvent(new CustomEvent('kloudz:intelligence:event', { detail: event }));
    if (eventQueue.length >= 18) flushEvents();
    clearTimeout(eventTimer);
    eventTimer = w.setTimeout(flushEvents, 5000);
  };

  const trimMap = (map) => {
    const entries = Object.entries(map);
    if (entries.length <= MAX_AFFINITY_KEYS) return map;
    return Object.fromEntries(entries.sort((a,b) => b[1] - a[1]).slice(0, MAX_AFFINITY_KEYS));
  };
  const bump = (map, key, amount) => {
    const normalized = lower(key).slice(0, 64);
    if (!normalized) return;
    map[normalized] = clamp((num(map[normalized]) || 0) + amount, -12, 30);
  };

  const candidateFromNode = (node) => {
    const tags = clean(node.dataset.kziTags || '', 600).split('|').map(lower).filter(Boolean).slice(0, 16);
    const createdRaw = num(node.dataset.kziCreatedAt);
    const createdAt = createdRaw > 0 && createdRaw < 100000000000 ? createdRaw * 1000 : createdRaw;
    const price = clamp(node.dataset.kziPrice, 0, 100000000);
    const compareAt = clamp(node.dataset.kziCompareAt, 0, 100000000);
    const ageDays = createdAt ? Math.max(0, (now() - createdAt) / DAY) : 365;
    return {
      node, handle: clean(node.dataset.kziHandle, 80), type: clean(node.dataset.kziType || 'Product', 64),
      vendor: clean(node.dataset.kziVendor, 64), tags, price, compareAt, createdAt, ageDays,
      available: node.dataset.kziAvailable === 'true', isNew: ageDays <= 45,
      isDeal: compareAt > price && price > 0, score: 0, components: {}, reason: 'Relevant catalog pick'
    };
  };

  const learnCandidate = (candidate, action, amount) => {
    if (!candidate || !candidate.handle) return;
    bump(state.affinities.types, candidate.type, amount);
    bump(state.affinities.vendors, candidate.vendor, amount * .75);
    candidate.tags.slice(0, 6).forEach((tag) => bump(state.affinities.tags, tag, amount * .45));
    state.affinities.types = trimMap(state.affinities.types);
    state.affinities.vendors = trimMap(state.affinities.vendors);
    state.affinities.tags = trimMap(state.affinities.tags);
    if (candidate.price > 0 && amount > 0) {
      state.price.sum += candidate.price;
      state.price.count += 1;
      state.price.min = state.price.min ? Math.min(state.price.min, candidate.price) : candidate.price;
      state.price.max = Math.max(state.price.max, candidate.price);
    }
    state.sequence.push({ handle: candidate.handle, type: candidate.type, vendor: candidate.vendor, tags: candidate.tags.slice(0, 8), price: candidate.price, action, at: now() });
    state.sequence = state.sequence.slice(-MAX_SEQUENCE);
    persist();
  };

  const findCandidate = (target) => {
    const node = target && target.closest ? target.closest('[data-kzi-candidate]') : null;
    return node ? candidateFromNode(node) : null;
  };

  const armWeights = (arm) => ({
    balanced: { affinity: .34, price: .13, fresh: .14, value: .14, novelty: .13, explore: .12 },
    freshness: { affinity: .23, price: .08, fresh: .34, value: .1, novelty: .13, explore: .12 },
    value: { affinity: .24, price: .18, fresh: .1, value: .3, novelty: .08, explore: .1 },
    discovery: { affinity: .15, price: .08, fresh: .19, value: .1, novelty: .26, explore: .22 }
  }[arm] || { affinity: .34, price: .13, fresh: .14, value: .14, novelty: .13, explore: .12 });

  const ensureBandit = (key) => {
    if (!state.bandit[key] || typeof state.bandit[key] !== 'object') state.bandit[key] = {};
    ARMS.forEach((arm) => {
      const current = state.bandit[key][arm];
      state.bandit[key][arm] = current && typeof current === 'object' ? { n: clamp(current.n, 0, 100000), reward: clamp(current.reward, 0, 1000000) } : { n: 0, reward: 0 };
    });
    return state.bandit[key];
  };

  const chooseArm = (objective) => {
    const key = contextKey();
    const bandit = ensureBandit(key);
    const objectiveArm = ARMS.includes(objective) ? objective : 'balanced';
    const unexplored = ARMS.filter((arm) => bandit[arm].n === 0);
    let chosen;
    if (unexplored.length) chosen = unexplored[Math.floor(hash(`${session.id}|${key}`) * unexplored.length)];
    else if (hash(`${session.id}|explore|${state.pageviews}`) < runtimeExploration) chosen = ARMS[Math.floor(hash(`${session.id}|arm|${state.pageviews}`) * ARMS.length)];
    else {
      const total = ARMS.reduce((sum, arm) => sum + bandit[arm].n, 0);
      chosen = ARMS.map((arm) => {
        const record = bandit[arm];
        const mean = record.reward / Math.max(1, record.n);
        const confidence = Math.sqrt((2 * Math.log(Math.max(2, total))) / Math.max(1, record.n));
        return { arm, score: mean + confidence + (arm === objectiveArm ? .16 : 0) };
      }).sort((a,b) => b.score - a.score)[0].arm;
    }
    bandit[chosen].n += 1;
    persist();
    return { arm: chosen, context: key };
  };

  const rewardArm = (surface, amount) => {
    if (!surface || !surface.dataset.kziArm || !surface.dataset.kziBanditContext) return;
    const bandit = ensureBandit(surface.dataset.kziBanditContext);
    const arm = surface.dataset.kziArm;
    if (bandit[arm]) bandit[arm].reward = clamp(bandit[arm].reward + amount, 0, 1000000);
    persist();
  };

  const scoreCandidates = (candidates, arm) => {
    const weights = armWeights(arm);
    const average = state.price.count ? state.price.sum / state.price.count : 0;
    const recent = new Set(state.sequence.slice(-16).map((item) => item.handle));
    const disliked = new Set(state.disliked);
    candidates.forEach((candidate) => {
      const typeAffinity = num(state.affinities.types[lower(candidate.type)]);
      const vendorAffinity = num(state.affinities.vendors[lower(candidate.vendor)]);
      const tagAffinity = candidate.tags.reduce((sum, tag) => sum + num(state.affinities.tags[tag]), 0) / Math.max(1, candidate.tags.length);
      const affinity = clamp((typeAffinity + vendorAffinity * .75 + tagAffinity * .8) / 10, -1, 1);
      const priceFit = average && candidate.price ? Math.max(0, 1 - Math.abs(candidate.price - average) / Math.max(average, candidate.price)) : .5;
      const fresh = Math.exp(-candidate.ageDays / 90);
      const value = candidate.isDeal ? clamp((candidate.compareAt - candidate.price) / candidate.compareAt, 0, .8) / .8 : 0;
      const novelty = recent.has(candidate.handle) ? 0 : 1;
      const explore = hash(`${session.id}|${arm}|${candidate.handle}`);
      candidate.components = { affinity, priceFit, fresh, value, novelty, explore };
      candidate.score = candidate.available ? (
        affinity * weights.affinity + priceFit * weights.price + fresh * weights.fresh + value * weights.value + novelty * weights.novelty + explore * weights.explore
      ) : -1000;
      if (disliked.has(candidate.handle)) candidate.score -= 5;
      if (affinity > .2) candidate.reason = 'Matches this session';
      else if (candidate.isNew && fresh > .6) candidate.reason = 'New to the catalog';
      else if (candidate.isDeal) candidate.reason = 'Strong current catalog value';
      else if (novelty > .5 && arm === 'discovery') candidate.reason = 'Explore something different';
      else if (priceFit > .76 && average) candidate.reason = 'Near your browsing range';
      else candidate.reason = 'Relevant catalog pick';
    });
    return candidates.sort((a,b) => b.score - a.score);
  };

  const constrain = (ordered, limit) => {
    const maxPerType = clamp(config.maxPerCategory || 4, 1, 8);
    const selected = [];
    const deferred = [];
    const typeCounts = {};
    ordered.filter((item) => item.available).forEach((item) => {
      const key = lower(item.type || 'product');
      if ((typeCounts[key] || 0) < maxPerType) { selected.push(item); typeCounts[key] = (typeCounts[key] || 0) + 1; }
      else deferred.push(item);
    });
    const ensureExposure = (predicate) => {
      const match = ordered.find((item) => item.available && predicate(item));
      if (!match || selected.slice(0, limit).includes(match)) return;
      const replaceAt = Math.max(0, Math.min(limit - 1, selected.length - 1));
      const removed = selected[replaceAt];
      selected.splice(replaceAt, 1, match);
      const duplicateAt = selected.indexOf(match, replaceAt + 1);
      if (duplicateAt >= 0) selected.splice(duplicateAt, 1, removed);
    };
    ensureExposure((item) => item.isNew);
    ensureExposure((item) => item.isDeal);
    const seen = new Set();
    return [...selected, ...deferred, ...ordered.filter((item) => !item.available)].filter((item) => {
      if (seen.has(item.handle)) return false;
      seen.add(item.handle);
      return true;
    });
  };

  const health = (candidates, ordered, limit) => {
    const visible = ordered.slice(0, limit).filter((item) => item.available);
    const types = new Set(visible.map((item) => lower(item.type)).filter(Boolean));
    const newCount = visible.filter((item) => item.isNew).length;
    const dealCount = visible.filter((item) => item.isDeal).length;
    const counts = {};
    visible.forEach((item) => { const key = lower(item.type || 'product'); counts[key] = (counts[key] || 0) + 1; });
    const violations = Object.values(counts).filter((count) => count > clamp(config.maxPerCategory || 4, 1, 8)).length + visible.filter((item) => !item.available).length;
    return {
      total: candidates.length, available: candidates.filter((item) => item.available).length, visible: visible.length,
      types: types.size, newCount, dealCount, density: limit ? visible.length / limit : 0, violations,
      label: visible.length >= Math.min(limit, 8) && types.size >= Math.min(3, visible.length) ? 'Healthy mix' : visible.length ? 'Limited mix' : 'No available items'
    };
  };

  const autonomyEnabled = () => Boolean(config.autonomy && config.autonomy.enabled);
  const autonomyFloor = () => clampConfig(config.autonomy?.confidenceFloor, .6, .95, .8);
  const autonomyLimit = () => Math.round(clampConfig(config.autonomy?.maxActionsPerSession, 2, 20, 8));
  const decisionConfidence = (metrics) => clamp(
    .36 + (metrics.density * .28) + (Math.min(metrics.types, 5) / 5 * .18) +
    ((metrics.available / Math.max(1, metrics.total)) * .1) + (metrics.violations === 0 ? .08 : 0),
    0, 1
  );

  const policyCheck = (candidates, ordered, limit) => {
    const metrics = health(candidates, ordered, limit);
    const visible = ordered.slice(0, limit).filter((item) => item.available);
    const handles = visible.map((item) => item.handle);
    const duplicateHandles = handles.length - new Set(handles).size;
    const hardViolations = metrics.violations + duplicateHandles;
    return {
      ok: hardViolations === 0 && visible.length > 0,
      confidence: decisionConfidence(metrics),
      metrics,
      hardViolations,
      reasons: [
        ...(visible.length ? [] : ['no_available_candidates']),
        ...(metrics.violations ? ['category_or_inventory_constraint'] : []),
        ...(duplicateHandles ? ['duplicate_candidate'] : [])
      ]
    };
  };

  const decisionRecord = (surface, action, reason, confidence, result) => {
    const record = {
      id: uid(), at: now(), surface: clean(surface?.dataset.kziSurface || 'global', 48),
      action: clean(action, 32), reason: clean(reason, 80), confidence: clamp(confidence, 0, 1), result: clean(result, 32)
    };
    state.decisions.push(record);
    state.decisions = state.decisions.slice(-40);
    state.autonomy.lastDecisionAt = record.at;
    state.autonomy.cycles += 1;
    if (result === 'applied') {
      state.autonomy.actions += 1;
      session.autonomyActions = clampConfig(session.autonomyActions + 1, 0, 1000, 0);
    }
    emit('autonomous_decision', {
      surface: record.surface, action: record.action, reason: record.reason,
      confidence: Math.round(record.confidence * 100) / 100, result: record.result,
      policy: 'bounded_zero_touch'
    });
    updateConsole({
      autonomy: autonomyEnabled() ? 'Zero-touch active' : 'Emergency stop',
      decisions: state.autonomy.actions,
      rollbacks: state.autonomy.rollbacks,
      state: result === 'applied' ? 'Autopilot operating' : 'Policy intervention'
    });
    persist();
    return record;
  };

  const blockDecision = (surface, action, reason, confidence = 0) => {
    state.autonomy.blocked += 1;
    emit('policy_violation', {
      surface: clean(surface?.dataset.kziSurface || 'global', 48), action: clean(action, 32),
      reason: clean(reason, 80), policy: 'fail_closed'
    });
    return decisionRecord(surface, action, reason, confidence, 'blocked');
  };

  const stableOrder = (surface, candidates) => {
    const handles = stableOrders.get(surface);
    if (!Array.isArray(handles) || !handles.length) return null;
    const byHandle = new Map(candidates.map((item) => [item.handle, item]));
    const output = handles.map((handle) => byHandle.get(handle)).filter(Boolean);
    candidates.forEach((item) => { if (!output.includes(item)) output.push(item); });
    return output;
  };

  const applyRanking = (surface, ordered, meta) => {
    const grid = surface.querySelector('[data-kzi-candidate-grid]');
    if (!grid) return null;
    const limit = clamp(grid.dataset.kziLimit || 12, 1, 24);
    const fragment = d.createDocumentFragment();
    ordered.forEach((candidate, index) => {
      const visible = index < limit && candidate.available;
      candidate.node.hidden = !visible;
      candidate.node.setAttribute('aria-hidden', String(!visible));
      candidate.node.style.setProperty('--kzi-order', String(index));
      const reason = candidate.node.querySelector('[data-kzi-reason]');
      if (reason) reason.textContent = clean(candidate.reason, 72) || 'Relevant catalog pick';
      fragment.appendChild(candidate.node);
    });
    grid.appendChild(fragment);
    surface.dataset.kziMode = meta.mode;
    surface.dataset.kziArm = meta.arm;
    surface.dataset.kziBanditContext = meta.context;
    surface.dataset.kziRanked = 'true';
    const status = surface.querySelector('[data-kzi-status]');
    if (status) {
      status.lastChild.textContent = meta.mode === 'remote' ? ' Live autonomous model' :
        meta.mode === 'rollback' ? ' Safe state restored automatically' :
          meta.mode === 'fallback' ? ' Autonomous local fallback' : ' Zero-touch on-device decision';
    }
    const metrics = health(ordered, ordered, limit);
    updateConsole({
      ...metrics, mode: meta.mode, arm: meta.arm, latency: meta.latency, storage: storageMode,
      autonomy: autonomyEnabled() ? 'Zero-touch active' : 'Emergency stop',
      decisions: state.autonomy.actions, rollbacks: state.autonomy.rollbacks
    });
    emit('engine_health', { surface: clean(surface.dataset.kziSurface, 48), mode: meta.mode, arm: meta.arm, latencyMs: meta.latency, ...metrics });
    observeImpressions(surface);
    return metrics;
  };

  const remoteRank = async (surface, candidates, localOrder, arm, context) => {
    if (!config.remoteRanking || !privacy.analytics || !endpoint('/v1/rank')) return null;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timeout = w.setTimeout(() => controller && controller.abort(), clamp(config.rankingTimeout || 450, 100, 1500));
    try {
      const response = await fetch(endpoint('/v1/rank'), {
        method: 'POST', credentials: 'omit', signal: controller ? controller.signal : undefined,
        headers: { 'Content-Type': 'application/json', 'X-Kloudz-Protocol': 'kloudz-rank/1' },
        body: JSON.stringify({
          protocol: 'kloudz-rank/1', surface: clean(surface.dataset.kziSurface, 48), objective: clean(surface.dataset.kziObjective, 24),
          arm, context, features: safeSnapshot(),
          candidates: candidates.map((item) => ({ handle: item.handle, type: item.type, vendor: item.vendor, tags: item.tags, priceCents: item.price, compareAtCents: item.compareAt, createdAt: item.createdAt || null, available: item.available }))
        })
      });
      if (!response.ok) return null;
      const data = await response.json();
      const ranking = Array.isArray(data.ranking) ? data.ranking : [];
      const byHandle = new Map(candidates.map((item) => [item.handle, item]));
      const seen = new Set();
      const ranked = [];
      ranking.forEach((entry) => {
        const handle = clean(typeof entry === 'string' ? entry : entry && entry.handle, 80);
        const item = byHandle.get(handle);
        if (!item || seen.has(handle) || !item.available) return;
        seen.add(handle);
        if (entry && typeof entry === 'object' && entry.reason) item.reason = clean(entry.reason, 72);
        ranked.push(item);
      });
      localOrder.forEach((item) => { if (!seen.has(item.handle)) ranked.push(item); });
      return ranking.length ? constrain(ranked, clamp(surface.querySelector('[data-kzi-candidate-grid]')?.dataset.kziLimit || 12, 1, 24)) : null;
    } catch (_) { return null; }
    finally { clearTimeout(timeout); }
  };

  const remoteAutonomyDecision = async (surface) => {
    if (!autonomyEnabled() || !config.autonomy?.remoteOrchestration || !privacy.analytics || !endpoint('/v1/orchestrate')) return null;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timeout = w.setTimeout(() => controller && controller.abort(), clamp(config.rankingTimeout || 450, 100, 1500));
    try {
      const response = await fetch(endpoint('/v1/orchestrate'), {
        method: 'POST', credentials: 'omit', signal: controller ? controller.signal : undefined,
        headers: { 'Content-Type': 'application/json', 'X-Kloudz-Protocol': 'kloudz-autonomy/1' },
        body: JSON.stringify({
          protocol: 'kloudz-autonomy/1', policy: 'bounded_zero_touch',
          surface: clean(surface.dataset.kziSurface, 48), objective: clean(surface.dataset.kziObjective, 24),
          features: safeSnapshot(), allowedActions: config.autonomy.allowedStorefrontActions || []
        })
      });
      if (!response.ok) return null;
      const data = await response.json();
      const action = data && typeof data.action === 'object' ? data.action : null;
      if (!action) return null;
      const type = lower(action.type).replace(/[^a-z_]/g, '').slice(0, 32);
      const confidence = clamp(action.confidence, 0, 1);
      const allowed = new Set(config.autonomy.allowedStorefrontActions || []);
      if (!allowed.has(type)) {
        blockDecision(surface, type || 'unknown', 'action_not_allowlisted', confidence);
        return null;
      }
      if (confidence < autonomyFloor()) {
        blockDecision(surface, type, 'below_confidence_floor', confidence);
        return null;
      }
      if (type === 'set_objective') {
        const objective = lower(action.value);
        if (!ARMS.includes(objective)) { blockDecision(surface, type, 'invalid_objective', confidence); return null; }
        surface.dataset.kziObjective = objective;
      }
      if (type === 'set_exploration') runtimeExploration = clampConfig(action.value, 0, .2, runtimeExploration);
      return { type, confidence, restoreStable: type === 'restore_stable' };
    } catch (_) { return null; }
    finally { clearTimeout(timeout); }
  };

  async function rankSurface(surface, force = false, reason = 'surface_activation') {
    if (!surface || (rankedSurfaces.has(surface) && !force)) return;
    surfaces.add(surface);
    rankedSurfaces.add(surface);
    const started = performance.now();
    const candidates = Array.from(surface.querySelectorAll('[data-kzi-candidate]')).map(candidateFromNode);
    if (!candidates.length) return;
    surface.dataset.kziAutonomy = autonomyEnabled() ? 'active' : 'stopped';
    const orchestrated = await remoteAutonomyDecision(surface);
    const choice = chooseArm(lower(surface.dataset.kziObjective || 'balanced'));
    surface.dataset.kziArm = choice.arm;
    surface.dataset.kziBanditContext = choice.context;
    const scored = scoreCandidates(candidates, choice.arm);
    const limit = clamp(surface.querySelector('[data-kzi-candidate-grid]')?.dataset.kziLimit || 12, 1, 24);
    let ordered = constrain(scored, limit);
    let mode = 'local';
    if (orchestrated?.restoreStable) {
      const restored = stableOrder(surface, candidates);
      if (restored) { ordered = restored; mode = 'rollback'; }
    }
    const remote = await remoteRank(surface, candidates, ordered, choice.arm, choice.context);
    if (remote && !orchestrated?.restoreStable) { ordered = remote; mode = 'remote'; }
    else if (config.remoteRanking && !orchestrated?.restoreStable) mode = 'fallback';
    const latency = Math.max(0, Math.round(performance.now() - started));
    let policy = policyCheck(candidates, ordered, limit);
    if (!policy.ok) {
      const failureReasons = [...policy.reasons];
      const restored = stableOrder(surface, candidates) || constrain(
        [...candidates].sort((a,b) => Number(b.available) - Number(a.available)), limit
      );
      const fallbackPolicy = policyCheck(candidates, restored, limit);
      if (config.autonomy?.automaticRollback && fallbackPolicy.ok) {
        ordered = restored;
        mode = 'rollback';
        policy = fallbackPolicy;
        state.autonomy.rollbacks += 1;
        emit('automatic_rollback', {
          surface: clean(surface.dataset.kziSurface, 48),
          reasons: failureReasons, policy: 'fail_closed'
        });
        applyRanking(surface, ordered, { mode, arm: choice.arm, context: choice.context, latency });
        decisionRecord(surface, 'restore_stable', reason, policy.confidence, 'applied');
      } else {
        blockDecision(surface, 'rerank', failureReasons.join(',') || 'hard_policy_failure', policy.confidence);
      }
      return;
    }
    const metrics = applyRanking(surface, ordered, { mode, arm: choice.arm, context: choice.context, latency });
    stableOrders.set(surface, ordered.map((item) => item.handle));
    surface.dataset.kziFingerprint = surfaceFingerprint(surface);
    state.autonomy.lastFingerprint = surface.dataset.kziFingerprint;
    if (autonomyEnabled()) {
      decisionRecord(
        surface,
        orchestrated?.type || 'rerank',
        orchestrated ? 'remote_orchestrator' : reason,
        orchestrated?.confidence ?? policy.confidence,
        'applied'
      );
    } else updateConsole({ autonomy: 'Emergency stop', ...metrics });
    const copyVariant = state.experiments.adaptiveCopy || (hash(`${session.id}|copy`) < .5 ? 'adaptive' : 'signal');
    state.experiments.adaptiveCopy = copyVariant;
    const copy = surface.querySelector('[data-kzi-experiment-copy]');
    if (copy) copy.textContent = copyVariant === 'signal' ? 'AI MARKET SIGNAL' : 'AUTONOMOUS EDIT';
    emit('experiment_exposure', { experiment: 'adaptive_rail_copy', variant: copyVariant, surface: clean(surface.dataset.kziSurface, 48) });
    emit('ranking_response', {
      surface: clean(surface.dataset.kziSurface, 48), mode, arm: choice.arm, candidates: candidates.length,
      latencyMs: latency, autonomous: autonomyEnabled(), confidence: Math.round(policy.confidence * 100) / 100
    });
    persist();
  }

  let impressionObserver;
  const observeImpressions = (surface) => {
    if (!('IntersectionObserver' in w)) return;
    if (!impressionObserver) {
      impressionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < .5 || entry.target.hidden) return;
          const candidate = candidateFromNode(entry.target);
          const parent = entry.target.closest('[data-kzi-surface]');
          const key = `${parent?.dataset.kziSurface || 'surface'}|${candidate.handle}`;
          if (impressionKeys.has(key)) return;
          impressionKeys.add(key);
          state.outcomes.impressions += 1;
          emit('impression', { surface: clean(parent?.dataset.kziSurface, 48), handle: candidate.handle, position: Array.from(entry.target.parentElement.children).indexOf(entry.target), arm: clean(parent?.dataset.kziArm, 24) });
          impressionObserver.unobserve(entry.target);
          persist();
        });
      }, { threshold: [.5] });
    }
    surface.querySelectorAll('[data-kzi-candidate]:not([hidden])').forEach((node) => impressionObserver.observe(node));
  };

  const classifySearch = (value) => {
    const query = lower(value).replace(/[^a-z0-9\s-]/g, ' ').replace(/\s+/g, ' ').trim();
    const tokens = query ? query.split(' ').filter(Boolean).slice(0, 12) : [];
    const intents = [];
    const has = (...words) => words.some((word) => tokens.includes(word));
    if (has('new', 'latest', 'fresh', 'arrival', 'arrivals')) intents.push('freshness');
    if (has('deal', 'deals', 'sale', 'discount', 'cheap', 'value')) intents.push('value');
    if (has('glass', 'grinder', 'grinders', 'battery', 'batteries', 'vape', 'vapes', 'hookah', 'scale', 'scales')) intents.push('category');
    if (!intents.length && tokens.length) intents.push('open_discovery');
    const count = state.search.count;
    state.search.count += 1;
    state.search.averageLength = ((state.search.averageLength * count) + tokens.length) / state.search.count;
    intents.forEach((intent) => bump(state.search.intents, intent, 1));
    persist();
    return { tokenCount: tokens.length, intents };
  };

  const surfaceFingerprint = (surface) => {
    const candidates = Array.from(surface.querySelectorAll('[data-kzi-candidate]')).map((node) => [
      node.dataset.kziHandle, node.dataset.kziAvailable, node.dataset.kziPrice, node.dataset.kziCompareAt
    ].join(':'));
    const recent = state.sequence.slice(-4).map((item) => `${item.handle}:${item.action}`).join('|');
    const outcomes = Object.values(state.outcomes).join(':');
    return hash(`${surface.dataset.kziObjective}|${candidates.join('|')}|${recent}|${outcomes}|${state.search.count}`).toString(36);
  };

  const autonomyCycle = async (reason = 'scheduled_monitor', targetSurface = null) => {
    if (!autonomyEnabled() || d.visibilityState === 'hidden') return;
    const targets = targetSurface ? [targetSurface] : Array.from(surfaces);
    const signalReasons = new Set(['customer_feedback', 'quick_view_signal', 'save_signal', 'compare_signal', 'cart_success_signal', 'api_cycle', 'design_refresh']);
    for (const surface of targets) {
      if (!surface || surface.dataset.kziRanked !== 'true') continue;
      const fingerprint = surfaceFingerprint(surface);
      if (!signalReasons.has(reason) && surface.dataset.kziFingerprint === fingerprint) continue;
      if (session.autonomyActions >= autonomyLimit()) {
        if (surface.dataset.kziCircuit !== 'open') {
          surface.dataset.kziCircuit = 'open';
          blockDecision(surface, 'rerank', 'session_action_budget_reached', 1);
          updateConsole({ autonomy: 'Circuit paused', state: 'Safe state retained' });
        }
        continue;
      }
      surface.dataset.kziCircuit = 'closed';
      surface.dataset.kziFingerprint = fingerprint;
      state.autonomy.lastFingerprint = fingerprint;
      await rankSurface(surface, true, reason);
    }
  };

  const requestAutonomy = (surface, reason) => {
    if (!surface || !autonomyEnabled()) return;
    clearTimeout(autonomyDebounce);
    autonomyDebounce = w.setTimeout(() => autonomyCycle(reason, surface), 240);
  };

  const startAutonomyMonitor = () => {
    clearInterval(autonomyTimer);
    if (!autonomyEnabled()) return;
    const interval = Math.round(clampConfig(config.autonomy?.decisionInterval, 15000, 120000, 30000));
    autonomyTimer = w.setInterval(() => autonomyCycle('scheduled_monitor'), interval);
  };

  const feedback = (button) => {
    const candidate = findCandidate(button);
    const value = button.dataset.kziFeedback;
    if (!candidate || !['positive', 'negative'].includes(value)) return;
    const positive = value === 'positive';
    learnCandidate(candidate, 'feedback', positive ? 2 : -2);
    button.closest('.kzi-feedback')?.querySelectorAll('button').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
    if (positive) state.disliked = state.disliked.filter((handle) => handle !== candidate.handle);
    else if (!state.disliked.includes(candidate.handle)) state.disliked.push(candidate.handle);
    state.disliked = state.disliked.slice(-30);
    const surface = button.closest('[data-kzi-surface]');
    rewardArm(surface, positive ? .45 : 0);
    emit('feedback', { handle: candidate.handle, value, surface: clean(surface?.dataset.kziSurface, 48) });
    persist();
    if (surface) requestAutonomy(surface, 'customer_feedback');
  };

  const clearLearning = () => {
    removeKey(sessionStore, STATE_KEY);
    removeKey(localStore, STATE_KEY);
    state = defaults();
    state.pageviews = 1;
    session.autonomyActions = 0;
    d.querySelectorAll('[data-kzi-feedback]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
    persist();
    surfaces.forEach((surface) => {
      surface.dataset.kziCircuit = 'closed';
      rankSurface(surface, true, 'privacy_reset');
    });
    emit('personalization_reset', { source: 'customer_control' });
    updateConsole({ state: 'Learning cleared', storage: storageMode });
  };

  const updateOutcome = (key) => {
    if (Object.prototype.hasOwnProperty.call(state.outcomes, key)) state.outcomes[key] += 1;
  };

  d.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const feedbackButton = target.closest('[data-kzi-feedback]');
    if (feedbackButton) { event.preventDefault(); feedback(feedbackButton); return; }
    if (target.closest('[data-kzi-reset],[data-kzi-console-clear]')) { event.preventDefault(); clearLearning(); return; }
    if (target.closest('[data-kzi-console-refresh]')) { surfaces.forEach((surface) => rankSurface(surface, true, 'design_refresh')); return; }
    if (target.closest('[data-kzi-console-export]')) { exportSnapshot(); return; }

    const candidate = findCandidate(target);
    const surface = target.closest('[data-kzi-surface]');
    if (target.closest('[data-kzf-quick-view]')) {
      pendingProduct = candidate;
      updateOutcome('quickViews');
      learnCandidate(candidate, 'quick_view', 1.1);
      rewardArm(surface, .28);
      emit('quick_view', { handle: candidate?.handle || clean(target.closest('[data-kzf-quick-view]')?.dataset.handle, 80), surface: clean(surface?.dataset.kziSurface, 48) });
      requestAutonomy(surface, 'quick_view_signal');
      return;
    }
    if (target.closest('[data-kzc-save-product]')) {
      updateOutcome('saves'); learnCandidate(candidate, 'save', 1.45); rewardArm(surface, .5);
      emit('save', { handle: candidate?.handle || '', surface: clean(surface?.dataset.kziSurface, 48) });
      requestAutonomy(surface, 'save_signal');
      return;
    }
    if (target.closest('[data-kzc-compare-product],[data-kzc-compare-card],.kzc-card-compare')) {
      updateOutcome('compares'); learnCandidate(candidate, 'compare', 1.2); rewardArm(surface, .4);
      emit('compare', { handle: candidate?.handle || '', surface: clean(surface?.dataset.kziSurface, 48) });
      requestAutonomy(surface, 'compare_signal');
      return;
    }
    const productLink = target.closest('a[href*="/products/"]');
    if (productLink) {
      pendingProduct = candidate;
      updateOutcome('clicks'); learnCandidate(candidate, 'product_click', .9); rewardArm(surface, .3);
      emit('product_click', { handle: candidate?.handle || clean(productLink.pathname?.split('/products/')[1]?.split(/[?#/]/)[0], 80), surface: clean(surface?.dataset.kziSurface, 48) });
    }
  }, true);

  d.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('form[role="search"],form[action*="/search"]')) {
      const input = form.querySelector('input[type="search"],input[name="q"]');
      const insight = classifySearch(input ? input.value : '');
      emit('search_submit', insight);
    }
    if (form.matches('[data-kzf-ajax-add],form[action*="/cart/add"]')) {
      const candidate = findCandidate(form) || pendingProduct;
      pendingProduct = candidate;
      emit('add_to_cart', { handle: candidate?.handle || '', phase: 'attempt' });
    }
  }, true);

  d.addEventListener('ajaxProduct:added', () => {
    const candidate = pendingProduct;
    updateOutcome('cartAdds');
    learnCandidate(candidate, 'add_to_cart', 2.4);
    const surface = candidate?.node?.closest('[data-kzi-surface]');
    rewardArm(surface, 1);
    emit('commerce_outcome', { outcome: 'cart_add_success', handle: candidate?.handle || '' });
    requestAutonomy(surface, 'cart_success_signal');
    pendingProduct = null;
  });

  function updateConsole(metrics = {}) {
    const consoleNode = d.querySelector('[data-kzi-console]');
    if (!consoleNode) return;
    const set = (selector, value) => { const node = consoleNode.querySelector(selector); if (node && value !== undefined) node.textContent = String(value); };
    set('[data-kzi-console-state]', metrics.state || (surfaces.size ? 'Autopilot monitoring' : 'Waiting for a surface'));
    set('[data-kzi-console-mode]', metrics.mode ? `${metrics.mode[0].toUpperCase()}${metrics.mode.slice(1)} ranking` : undefined);
    set('[data-kzi-console-autonomy]', metrics.autonomy);
    set('[data-kzi-console-decisions]', metrics.decisions !== undefined ? `${metrics.decisions} actions` : undefined);
    set('[data-kzi-console-rollbacks]', metrics.rollbacks !== undefined ? `${metrics.rollbacks} automatic` : undefined);
    set('[data-kzi-console-candidates]', metrics.total);
    set('[data-kzi-console-available]', metrics.available);
    set('[data-kzi-console-diversity]', metrics.types !== undefined ? `${metrics.types} types` : undefined);
    set('[data-kzi-console-health]', metrics.label);
    set('[data-kzi-console-arm]', metrics.arm ? `${metrics.arm[0].toUpperCase()}${metrics.arm.slice(1)}` : undefined);
    set('[data-kzi-console-latency]', metrics.latency !== undefined ? `${metrics.latency} ms` : undefined);
    set('[data-kzi-console-storage]', metrics.storage);
    set('[data-kzi-console-constraints]', metrics.violations !== undefined ? `${metrics.violations} violations` : undefined);
  }

  function exportSnapshot() {
    const payload = {
      exportedAt: new Date().toISOString(), protocol: 'kloudz-safe-snapshot/1',
      features: safeSnapshot(), guardrails: config.guardrails || {},
      autonomy: {
        policy: config.autonomy || {},
        recentDecisions: state.decisions.slice(-20)
      }
    };
    const url = URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }));
    const link = d.createElement('a');
    link.href = url;
    link.download = 'kloudz-merchandising-snapshot.json';
    link.click();
    w.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  const initSurface = (surface) => {
    if (!surface || surface.dataset.kziObserved === 'true') return;
    surface.dataset.kziObserved = 'true';
    surfaces.add(surface);
    if (autonomyEnabled()) {
      const start = () => rankSurface(surface, false, 'autonomous_boot');
      if ('requestIdleCallback' in w) w.requestIdleCallback(start, { timeout: 700 });
      else w.setTimeout(start, 0);
      return;
    }
    if ('IntersectionObserver' in w) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) { observer.disconnect(); rankSurface(surface); }
      }, { rootMargin: '500px 0px' });
      observer.observe(surface);
    } else rankSurface(surface);
  };

  const boot = (root = d) => {
    root.querySelectorAll?.('[data-kzi-surface]').forEach(initSurface);
    const designMode = Boolean(w.Shopify && w.Shopify.designMode) || d.documentElement.classList.contains('kloudz-design-mode');
    const consoleNode = d.querySelector('[data-kzi-console]');
    if (consoleNode && designMode && config.showDesignConsole) consoleNode.hidden = false;
    updateConsole({
      storage: storageMode,
      autonomy: autonomyEnabled() ? 'Zero-touch active' : 'Emergency stop',
      decisions: state.autonomy.actions,
      rollbacks: state.autonomy.rollbacks
    });
  };

  d.addEventListener('visitorConsentCollected', () => { refreshPrivacy(); persist(); });
  w.addEventListener('visitorConsentCollected', () => { refreshPrivacy(); persist(); });
  d.addEventListener('shopify:section:load', (event) => boot(event.target));
  d.addEventListener('visibilitychange', () => {
    if (d.visibilityState === 'hidden') emit('engagement', { dwellSeconds: Math.min(3600, Math.round((now() - session.startedAt) / 1000)) });
    else autonomyCycle('visibility_return');
  });
  w.addEventListener('pageshow', () => autonomyCycle('page_return'));
  w.addEventListener('pagehide', () => { clearInterval(autonomyTimer); persist(); flushEvents(true); });

  w.KloudzIntelligence = Object.freeze({
    version: VERSION,
    event: (name, payload) => emit(name, payload),
    rank: () => surfaces.forEach((surface) => rankSurface(surface, true, 'api_cycle')),
    runAutonomyCycle: () => autonomyCycle('api_cycle'),
    clear: clearLearning,
    getSafeSnapshot: () => JSON.parse(JSON.stringify(safeSnapshot())),
    getDecisionLedger: () => JSON.parse(JSON.stringify(state.decisions.slice(-20)))
  });

  boot();
  startAutonomyMonitor();
  persist();
  d.documentElement.dataset.kziStage = 'ready';
})();
